/**
 * Which strategy a running cycle is actually running.
 *
 * The migration from BASE v1.3 to BASE v2.0 is rolling: a cycle opened under
 * v1.3 keeps its rules until it closes, while new cycles open under v2.0. For
 * the length of that overlap the account is running two strategies at once,
 * and a screen that showed one version for all of them would be telling the
 * operator something untrue about money that is already at risk.
 *
 * Both records now carry it: the closed-cycle record always did, and the
 * running-slot projection was extended to read the identity the runtime binds
 * to each cycle. When the field is absent the screen says the version is not
 * reported, which is a different statement from naming the wrong one — and it
 * is still never derived from the slot, the physical pool or the symbol.
 */

/** The three versioned contracts that together define what the system does. */
export type ContractKind = "strategy" | "capital" | "allocation";

/** A version as the system names it, and which contract it versions. */
export type ContractVersion = {
  readonly kind: ContractKind;
  readonly version: string;
};

/**
 * The version a running cycle reports, or nothing.
 *
 * Read through the generated type now that the projection sends it. Still
 * tolerant of the shape rather than trusting it blindly: an empty string is
 * not a version, and treating one as a name would put an empty badge on a row.
 */
export function versionOf(slot: object): string | null {
  const raw = (slot as { strategy_version?: string | null }).strategy_version;
  return typeof raw === "string" && raw !== "" ? raw : null;
}

/**
 * The pool the cycle itself belongs to, never the slot's capacity label.
 *
 * `logical_pool` is what the runtime bound to the cycle: PRIME or PULSE under
 * BASE v2.0, the historical CORE/FAST/MAJOR under BASE v1.3. `pool` is where
 * the capacity was cut, and during the rolling migration the two disagree —
 * a slot cut as FAST holding a v2 cycle in PRIME. Absent identity means the
 * physical label is all there is, and it is shown as what it is rather than
 * promoted into an answer it cannot give.
 */
export function logicalPoolOf(slot: object): string | null {
  const raw = (slot as { logical_pool?: string | null }).logical_pool;
  return typeof raw === "string" && raw !== "" ? raw : null;
}

/**
 * The version as this product writes it.
 *
 * The server stores `base-v2.0`, which is an identifier. The screens say
 * `BASE v2.0`, which is the name the owner, the contract document and the
 * Strategy screen all use. Presentation only — the identifier is what travels
 * and what is compared, exactly as money travels exact and is rendered short.
 */
export function versionLabel(version: string): string {
  const match = /^base[ -]?v(\d+(?:\.\d+)?)$/i.exec(version);
  const number = match?.[1];
  // An identifier this does not recognise travels through unchanged. Renaming
  // one the product has never seen would be the browser inventing a name for
  // a strategy it knows nothing about.
  return number === undefined ? version : `BASE v${number}`;
}

export function contractVersionsOf(slot: object): {
  readonly strategy: string | null;
  readonly capital: string | null;
  readonly allocation: string | null;
} {
  const record = slot as {
    capital_policy_version?: unknown;
    allocation_policy_version?: unknown;
  };
  const read = (raw: unknown): string | null =>
    typeof raw === "string" && raw !== "" ? raw : null;
  return {
    strategy: versionOf(slot),
    capital: read(record.capital_policy_version),
    allocation: read(record.allocation_policy_version),
  };
}

/** Which generation a strategy version belongs to, by its own name. */
export function generationOfVersion(version: string): "v2" | "v1" | "unknown" {
  // Both spellings, because the identifier the server stores is `base-v2.0`
  // and the name this product writes is `BASE v2.0`. One function decides
  // which generation a version belongs to; two would eventually disagree.
  if (/^base[ -]?v2/i.test(version)) return "v2";
  if (/^base[ -]?v1/i.test(version)) return "v1";
  return "unknown";
}

/** The version the system opens new cycles under. */
export const CURRENT_STRATEGY_VERSION = "BASE v2.0";

/** The words for a version that is not reported, used identically everywhere. */
export const UNREPORTED_VERSION = "версия не сообщается";

/** A return, and the base it was measured against. */
export type CycleReturn = {
  readonly value: string;
  readonly base: "margin" | "notional";
};

/**
 * The return to show beside a cycle's PnL, and what it is a return on.
 *
 * BASE v2 arms its exit at five per cent of the margin actually invested, so
 * the margin figure is the one a reader compares against that threshold. At
 * two-times leverage the notional figure is exactly half of it, which is how
 * a cycle that had earned its stop appeared to be half way there — the
 * question behind the 2026-08-30 audit.
 *
 * BASE v1.3 compares against the notional and keeps it. Neither is wrong; a
 * screen showing one without saying which is.
 *
 * The server sends both; nothing is computed here.
 */
export function returnOf(slot: object): CycleReturn | null {
  const record = slot as {
    net_return?: string | null;
    net_roi_on_margin?: string | null;
  };
  const margin = record.net_roi_on_margin;
  const version = versionOf(slot);
  if (
    version !== null &&
    generationOfVersion(version) === "v2" &&
    typeof margin === "string" &&
    margin !== ""
  ) {
    return { value: margin, base: "margin" };
  }
  const notional = record.net_return;
  if (typeof notional === "string" && notional !== "") {
    return { value: notional, base: "notional" };
  }
  return null;
}

/** What the figure is measured against, said in words on the row itself. */
export const RETURN_TITLE: Record<CycleReturn["base"], string> = {
  margin: "NET ROI на вложенную маржу — база, с которой сравнивается порог +5 %",
  notional: "Результат цикла к вложенному в него капиталу",
};
